import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AlertTriangle, CalendarCheck, Check, ChevronLeft, ChevronRight, Clock, Edit,
    Fingerprint, IndianRupee, Loader2, LogIn, LogOut, MapPin, Save, X,
} from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { DialogModal } from "../../../components/ui/dialogModal";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import {
    BIOMETRIC_MAPPED_EMPLOYEES,
    EMPLOYEE_ATTENDANCE_DAILY,
    PAYROLL_ATTENDANCE_PENALTIES,
} from "../../../constants/api";
import { checkUserPermissionFunc } from "../../../components/common/CommonFunctions";
import { cn } from "../../../utils/tailwindUtils";
import { fieldControlClass } from "../../../components/ui/fieldStyles";
import { isOwnEmployeeProfile } from "../../../utils/employeeProfile";
import { EmployeeProfileSectionBlocked } from "../../../components/employee/EmployeeProfileAccessNotice";
import AttendanceEdit from "../../attendance/AttendanceEdit";

const { Option } = Select;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const STATUS_META = {
    present: {
        label: "Present", short: "P",
        cell: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800",
        pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
        dot: "bg-emerald-500",
    },
    TP: {
        label: "Transferred present", short: "TP",
        cell: "bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-100 dark:border-emerald-800",
        pill: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
        dot: "bg-emerald-600",
    },
    absent: {
        label: "Absent", short: "A",
        cell: "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800",
        pill: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
        dot: "bg-rose-500",
    },
    TA: {
        label: "Transferred absent", short: "TA",
        cell: "bg-rose-50 text-rose-900 border-rose-200 dark:bg-rose-900/30 dark:text-rose-100 dark:border-rose-800",
        pill: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
        dot: "bg-rose-600",
    },
    holiday: {
        label: "Holiday", short: "HD",
        cell: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800",
        pill: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
        dot: "bg-amber-500",
    },
    "on-duty": {
        label: "On duty", short: "OD",
        cell: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-200 dark:border-sky-800",
        pill: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
        dot: "bg-sky-500",
    },
    "week-off": {
        label: "Week off", short: "WO",
        cell: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800/40 dark:text-slate-300 dark:border-slate-700",
        pill: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
        dot: "bg-slate-400",
    },
    compensation: {
        label: "Compensation", short: "CO",
        cell: "bg-pink-50 text-pink-800 border-pink-200 dark:bg-pink-900/30 dark:text-pink-200 dark:border-pink-800",
        pill: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
        dot: "bg-pink-500",
    },
    WFH: {
        label: "Work from home", short: "WFH",
        cell: "bg-cyan-50 text-cyan-800 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-200 dark:border-cyan-800",
        pill: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
        dot: "bg-cyan-500",
    },
    leave: {
        label: "Leave", short: "L",
        cell: "bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-200 dark:border-violet-800",
        pill: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
        dot: "bg-violet-500",
    },
};

const statusMeta = (status) => STATUS_META[status] || null;

const formatClock = (value) => {
    if (!value || value === "NaT") return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("hh:mm A") : null;
};

const formatHours = (value) => {
    if (!value || value === "NaT") return null;
    const match = String(value).match(/(\d+):(\d+):(\d+)/);
    if (!match) return String(value);
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (!hours && !minutes) return null;
    if (!hours) return `${minutes}m`;
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const uniquePunches = (punches) => {
    const seen = new Set();
    return (Array.isArray(punches) ? punches : []).filter((punch) => {
        const time = punch.time || (punch.at ? dayjs(punch.at).format("hh:mm A") : "");
        const direction = String(punch.direction || "").toUpperCase();
        const key = `${time}|${direction}`;
        if (!time || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const viaLabel = (punch) => {
    if (punch.device_name) return punch.device_name;
    const via = String(punch.via || punch.source || "").toLowerCase();
    if (via === "web") return "Web";
    if (via === "mobile") return "Mobile";
    if (via === "biometric" || via === "device") return "Device";
    return punch.via || "";
};

const EmployeeAttendanceContent = ({ employeeID, employeeCode, readOnly = false }) => {
    const { fetch, create, update } = useApiMethods();
    const viewingOwnProfile = isOwnEmployeeProfile(employeeID);
    const attendanceEditRef = useRef();

    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_system_admin") || "false");
    const employeesPerm = checkUserPermissionFunc("employee", "employees", "SubMenu");
    const attendancePerm = checkUserPermissionFunc("attendance", "attendance_log", "SubMenu");
    const dayPerm = checkUserPermissionFunc("attendance", "day_wise_attendance", "SubMenu");
    const bioPerm = checkUserPermissionFunc("biometric", "biometric_devices", "SubMenu");
    const penaltyPerm = checkUserPermissionFunc("payroll", "Attendance Penalties", "SubMenu");

    const canView =
        viewingOwnProfile ||
        isAdmin ||
        attendancePerm.permission ||
        dayPerm.permission ||
        employeesPerm.permission;
    const canEditAttendance =
        !readOnly &&
        (isAdmin || (!viewingOwnProfile && (attendancePerm.edit || dayPerm.edit || employeesPerm.edit)));
    const canEditBio =
        !readOnly &&
        (isAdmin || (!viewingOwnProfile && (bioPerm.permission || bioPerm.edit || employeesPerm.edit)));
    const canEditPenalties =
        !readOnly && (isAdmin || penaltyPerm.edit || employeesPerm.edit);

    const [month, setMonth] = useState(() => dayjs());
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mapping, setMapping] = useState(null);
    const [mappingDraft, setMappingDraft] = useState({ code: "", name: "" });
    const [editingMap, setEditingMap] = useState(false);
    const [savingMap, setSavingMap] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [selectedDayRow, setSelectedDayRow] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [savingDay, setSavingDay] = useState(false);
    const [penalties, setPenalties] = useState([]);
    const [penaltiesLoading, setPenaltiesLoading] = useState(false);
    const [acceptingId, setAcceptingId] = useState(null);
    const [penaltyRow, setPenaltyRow] = useState(null);
    const [penaltyDialogOpen, setPenaltyDialogOpen] = useState(false);
    const [savingPenalty, setSavingPenalty] = useState(false);
    const [penaltyForm, setPenaltyForm] = useState({ mode: "deduction", deduction: 0.5, fine: 0 });

    const codeForMapping = employeeCode || "";

    const loadAttendance = useCallback(async () => {
        if (!employeeID) return;
        setLoading(true);
        try {
            const res = await fetch(EMPLOYEE_ATTENDANCE_DAILY, {
                employee_id: employeeID,
                month: month.month() + 1,
                year: month.year(),
                page_size: 50,
            });
            const payload = res?.data;
            const list = Array.isArray(payload) ? payload : (payload?.result || []);
            setRows(list.map((row) => ({ ...row, id: row.attendance_id || row.date })));
            if (res?.status === "fail") ToastMsg("error", res.message);
        } catch (error) {
            ToastMsg("error", error.message || "Could not load attendance");
            setRows([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID, month]);

    const loadPenalties = useCallback(async () => {
        if (!employeeID) return;
        setPenaltiesLoading(true);
        try {
            const res = await fetch(PAYROLL_ATTENDANCE_PENALTIES, {
                employee_id: employeeID,
                from_date: month.startOf("month").format("YYYY-MM-DD"),
                to_date: month.endOf("month").format("YYYY-MM-DD"),
                page_size: 100,
            });
            const payload = res?.data;
            const list = Array.isArray(payload) ? payload : (payload?.result || []);
            setPenalties(list);
            if (res?.status === "fail") ToastMsg("error", res.message);
        } catch (error) {
            ToastMsg("error", error.message || "Could not load penalties");
            setPenalties([]);
        } finally {
            setPenaltiesLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID, month]);

    const loadMapping = useCallback(async () => {
        if (!employeeID && !codeForMapping) return;
        try {
            const res = await fetch(BIOMETRIC_MAPPED_EMPLOYEES, {
                employee_id: employeeID || codeForMapping,
            });
            const list = Array.isArray(res?.data) ? res.data : [];
            const row = list[0] || null;
            setMapping(row);
            setMappingDraft({
                code: row?.code || "",
                name: row?.name || "",
            });
            setEditingMap(false);
        } catch (_) {
            setMapping(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID, codeForMapping]);

    useEffect(() => {
        if (!canView) return;
        loadAttendance();
        loadPenalties();
    }, [canView, loadAttendance, loadPenalties]);

    useEffect(() => {
        if (!canView) return;
        loadMapping();
    }, [canView, loadMapping]);

    const byDate = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            const key = row.date ? dayjs(row.date).format("YYYY-MM-DD") : null;
            if (key) map.set(key, row);
        });
        return map;
    }, [rows]);

    useEffect(() => {
        if (!rows.length) {
            setSelectedKey(null);
            return;
        }
        const today = dayjs().format("YYYY-MM-DD");
        const inMonth = month.format("YYYY-MM") === dayjs().format("YYYY-MM");
        if (inMonth && byDate.has(today)) {
            setSelectedKey(today);
            return;
        }
        const lastWorked = [...rows].reverse().find((row) => row.status);
        const fallback = lastWorked || rows[rows.length - 1];
        setSelectedKey(fallback?.date ? dayjs(fallback.date).format("YYYY-MM-DD") : null);
    }, [rows, month, byDate]);

    const calendarCells = useMemo(() => {
        const start = month.startOf("month");
        const daysInMonth = month.daysInMonth();
        const pad = start.day();
        const cells = [];
        for (let i = 0; i < pad; i += 1) cells.push(null);
        for (let day = 1; day <= daysInMonth; day += 1) {
            const date = month.date(day);
            const key = date.format("YYYY-MM-DD");
            cells.push({
                day,
                key,
                date,
                isToday: key === dayjs().format("YYYY-MM-DD"),
                record: byDate.get(key) || null,
            });
        }
        return cells;
    }, [month, byDate]);

    const selected = selectedKey ? byDate.get(selectedKey) : null;
    const selectedDate = selectedKey ? dayjs(selectedKey) : null;
    const selectedStatus = selected ? (selected.display_status ?? selected.status) : null;
    const selectedMeta = statusMeta(selectedStatus);
    const punches = uniquePunches(selected?.biometric_punches);

    const summary = useMemo(() => {
        const counts = { present: 0, absent: 0, leave: 0, recorded: rows.length };
        rows.forEach((row) => {
            const status = row.status || "";
            if (status === "present" || status === "on-duty" || status === "WFH") counts.present += 1;
            else if (status === "absent") counts.absent += 1;
            else if (status === "leave") counts.leave += 1;
        });
        return counts;
    }, [rows]);

    const startMapEdit = () => {
        setMappingDraft({
            code: mapping?.code || "",
            name: mapping?.name || "",
        });
        setEditingMap(true);
    };

    const cancelMapEdit = () => {
        setMappingDraft({
            code: mapping?.code || "",
            name: mapping?.name || "",
        });
        setEditingMap(false);
    };

    const saveMapping = async () => {
        const code = (mappingDraft.code || "").trim();
        const name = (mappingDraft.name || "").trim();
        if (!code) {
            ToastMsg("error", "Biometric code is required");
            return;
        }
        setSavingMap(true);
        try {
            const payload = {
                employee_id: codeForMapping || employeeID,
                code,
                name,
            };
            const res = mapping?.id
                ? await update(BIOMETRIC_MAPPED_EMPLOYEES, { ...payload, id: mapping.id })
                : await create(BIOMETRIC_MAPPED_EMPLOYEES, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Mapping saved");
                await loadMapping();
            } else {
                ToastMsg("error", res?.message || "Could not save mapping");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Could not save mapping");
        } finally {
            setSavingMap(false);
        }
    };

    const openDayEditor = (row) => {
        if (!row) return;
        setSelectedDayRow(row);
        setEditOpen(true);
    };

    const closeDay = () => {
        setEditOpen(false);
        setSelectedDayRow(null);
    };

    const saveDay = async () => {
        try {
            setSavingDay(true);
            if (attendanceEditRef.current) {
                await attendanceEditRef.current.handleSubmit();
            }
            setEditOpen(false);
            await loadAttendance();
            await loadPenalties();
        } catch (error) {
            console.error("Save failed:", error);
        } finally {
            setSavingDay(false);
        }
    };

    const openPenaltyEdit = (row) => {
        setPenaltyRow(row);
        setPenaltyForm({
            mode: row?.mode === "fine" ? "fine" : "deduction",
            deduction: Number(row?.deduction ?? 0.5),
            fine: Number(row?.fine ?? 0),
        });
        setPenaltyDialogOpen(true);
    };

    const acceptPenalty = async (row) => {
        if (!row?.attendance_id) return;
        setAcceptingId(row.attendance_id);
        try {
            const res = await update(PAYROLL_ATTENDANCE_PENALTIES, {
                action: "accept_suggested",
                attendance_ids: [row.attendance_id],
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Penalty accepted");
                await loadPenalties();
            } else {
                ToastMsg("error", res?.message || "Could not accept penalty");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Could not accept penalty");
        } finally {
            setAcceptingId(null);
        }
    };

    const savePenalty = async () => {
        if (!penaltyRow?.attendance_id) return;
        setSavingPenalty(true);
        try {
            const res = await update(PAYROLL_ATTENDANCE_PENALTIES, {
                attendance_id: penaltyRow.attendance_id,
                mode: penaltyForm.mode,
                deduction: penaltyForm.deduction,
                fine: penaltyForm.fine,
                penalty_type: penaltyRow.penalty_type,
                is_late: penaltyRow.is_late,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Penalty updated");
                setPenaltyDialogOpen(false);
                setPenaltyRow(null);
                await loadPenalties();
            } else {
                ToastMsg("error", res?.message || "Could not update penalty");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Could not update penalty");
        } finally {
            setSavingPenalty(false);
        }
    };

    const penaltyDateKey = (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "");

    if (!canView) {
        return (
            <EmployeeProfileSectionBlocked
                title="Attendance"
                action="view"
                message="You don't have permission to view this employee's attendance."
            />
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                        <CalendarCheck className="h-5 w-5 text-sidebar-primary" />
                        Attendance
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Month view for this employee, with reader mapping, punch trail, and attendance penalties.
                    </p>
                </div>
                <div className="flex items-center gap-1 rounded-lg border border-border bg-background px-1 py-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setMonth((prev) => prev.subtract(1, "month"))}
                        aria-label="Previous month"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <DatePicker
                        picker="month"
                        value={month}
                        allowClear={false}
                        suffixIcon={null}
                        format="MMMM YYYY"
                        onChange={(value) => value && setMonth(value)}
                        className="!w-[9.5rem] !border-0 !bg-transparent !shadow-none !h-8 [&_input]:!text-center [&_input]:!font-semibold [&_input]:!text-sm"
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setMonth((prev) => prev.add(1, "month"))}
                        aria-label="Next month"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {[
                    { label: "Present", value: summary.present, className: "text-emerald-700 dark:text-emerald-300" },
                    { label: "Absent", value: summary.absent, className: "text-rose-700 dark:text-rose-300" },
                    { label: "Leave", value: summary.leave, className: "text-violet-700 dark:text-violet-300" },
                    { label: "Days recorded", value: summary.recorded, className: "text-foreground" },
                ].map((stat) => (
                    <div key={stat.label} className="rounded-lg border border-border bg-muted/20 px-3 py-2.5">
                        <p className={cn("text-xl font-semibold tabular-nums leading-none", stat.className)}>{stat.value}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">{stat.label}</p>
                    </div>
                ))}
            </div>

            <div className="rounded-lg border border-border bg-muted/15 px-3 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                        <div className="h-8 w-8 flex-shrink-0 rounded-lg bg-sidebar-primary/10 text-sidebar-primary grid place-items-center">
                            <Fingerprint className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">Biometric mapping</p>
                            <p className="text-[11px] text-muted-foreground">
                                {canEditBio
                                    ? "Edit the reader code if punches are matching the wrong employee."
                                    : "Reader identity used to match this employee's punches."}
                            </p>
                        </div>
                    </div>
                    {canEditBio && (
                        <div className="flex items-center gap-1.5 shrink-0">
                            {editingMap ? (
                                <>
                                    <Button size="sm" variant="brand" disabled={savingMap} onClick={saveMapping}>
                                        {savingMap ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                        Save mapping
                                    </Button>
                                    <Button size="sm" variant="outline" disabled={savingMap} onClick={cancelMapEdit}>
                                        <X className="h-4 w-4" />
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <Button size="sm" variant="outline" onClick={startMapEdit}>
                                    <Edit className="h-4 w-4" />
                                    {mapping ? "Edit mapping" : "Add mapping"}
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {editingMap ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <label className="block min-w-0">
                            <span className="text-[11px] font-medium text-muted-foreground">Biometric code</span>
                            <input
                                type="text"
                                value={mappingDraft.code}
                                onChange={(e) => setMappingDraft((prev) => ({ ...prev, code: e.target.value }))}
                                placeholder="Code on the reader"
                                className={fieldControlClass("font-mono mt-1", { size: "sm" })}
                            />
                        </label>
                        <label className="block min-w-0">
                            <span className="text-[11px] font-medium text-muted-foreground">Name on device</span>
                            <input
                                type="text"
                                value={mappingDraft.name}
                                onChange={(e) => setMappingDraft((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder="Name stored on the reader"
                                className={fieldControlClass("mt-1", { size: "sm" })}
                            />
                        </label>
                        <div className="block min-w-0">
                            <span className="text-[11px] font-medium text-muted-foreground">Reader location</span>
                            <p className="mt-1 h-9 flex items-center gap-1 text-sm text-muted-foreground">
                                <MapPin className="h-3.5 w-3.5 shrink-0" />
                                {mapping?.location_name || "Not set"}
                            </p>
                        </div>
                    </div>
                ) : mapping ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="min-w-0">
                            <p className="text-[11px] font-medium text-muted-foreground">Biometric code</p>
                            <p className="mt-1 font-mono text-sm font-semibold text-gray-900 dark:text-white">
                                {mapping.code || "—"}
                            </p>
                        </div>
                        <div className="min-w-0">
                            <p className="text-[11px] font-medium text-muted-foreground">Name on device</p>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white truncate">
                                {mapping.name || "—"}
                            </p>
                        </div>
                        <div className="min-w-0">
                            <p className="text-[11px] font-medium text-muted-foreground">Reader location</p>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white flex items-center gap-1 truncate">
                                <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                {mapping.location_name || "—"}
                            </p>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        Not mapped to a reader. Use <span className="font-medium text-foreground">Add mapping</span> to enter the biometric code and name on device.
                    </p>
                )}
            </div>

            {loading && rows.length === 0 ? (
                <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
                </div>
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)] gap-4">
                    <div className="rounded-lg border border-border overflow-hidden">
                        <div className="grid grid-cols-7 border-b border-border bg-muted/40">
                            {WEEKDAYS.map((day) => (
                                <div
                                    key={day}
                                    className="py-2 text-center text-[11px] font-medium text-muted-foreground"
                                >
                                    {day}
                                </div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7">
                            {calendarCells.map((cell, index) => {
                                if (!cell) {
                                    return <div key={`pad-${index}`} className="min-h-[4.25rem] bg-muted/10" />;
                                }
                                const status = cell.record ? (cell.record.display_status ?? cell.record.status) : null;
                                const meta = statusMeta(status);
                                const isSelected = selectedKey === cell.key;
                                return (
                                    <button
                                        key={cell.key}
                                        type="button"
                                        onClick={() => setSelectedKey(cell.key)}
                                        className={cn(
                                            "min-h-[4.25rem] border-t border-r border-border p-1.5 text-left transition-colors",
                                            index % 7 === 0 && "border-l-0",
                                            meta ? meta.cell : "bg-background hover:bg-muted/40",
                                            isSelected && "ring-2 ring-inset ring-sidebar-primary",
                                            cell.isToday && !isSelected && "ring-1 ring-inset ring-sidebar-primary/40",
                                        )}
                                    >
                                        <div className="flex items-start justify-between gap-1">
                                            <span className={cn(
                                                "text-xs font-semibold tabular-nums",
                                                cell.isToday && "text-sidebar-primary",
                                            )}>
                                                {cell.day}
                                            </span>
                                            {meta && (
                                                <span className={cn("text-[10px] font-semibold", meta.pill, "px-1 rounded")}>
                                                    {meta.short}
                                                </span>
                                            )}
                                        </div>
                                        {cell.record?.check_in && (
                                            <p className="mt-1 text-[10px] text-muted-foreground leading-tight">
                                                {formatClock(cell.record.check_in)}
                                            </p>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex flex-wrap gap-3 px-3 py-2 border-t border-border bg-muted/20 text-[11px] text-muted-foreground">
                            {["present", "absent", "leave", "week-off", "holiday"].map((key) => (
                                <span key={key} className="inline-flex items-center gap-1.5">
                                    <span className={cn("h-2 w-2 rounded-full", STATUS_META[key].dot)} />
                                    {STATUS_META[key].label}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-lg border border-border p-4 flex flex-col">
                        {!selectedKey ? (
                            <div className="m-auto text-center text-sm text-muted-foreground">
                                <Clock className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                Select a day to see punches.
                            </div>
                        ) : (
                            <>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {selectedDate.format("dddd, D MMMM")}
                                        </p>
                                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                            {selectedMeta ? (
                                                <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full", selectedMeta.pill)}>
                                                    {selectedMeta.label}
                                                </span>
                                            ) : (
                                                <span className="text-[11px] text-muted-foreground">No attendance recorded</span>
                                            )}
                                            {selected?.shift && (
                                                <span className="text-[11px] text-muted-foreground">{selected.shift}</span>
                                            )}
                                        </div>
                                    </div>
                                    {canEditAttendance && selected && (
                                        <Button size="sm" variant="outline" onClick={() => openDayEditor(selected)}>
                                            <Edit className="h-4 w-4" />
                                            Edit day
                                        </Button>
                                    )}
                                </div>

                                <div className="mt-4 grid grid-cols-3 gap-2">
                                    {[
                                        { label: "Check in", value: formatClock(selected?.check_in), Icon: LogIn },
                                        { label: "Check out", value: formatClock(selected?.check_out), Icon: LogOut },
                                        { label: "Hours", value: formatHours(selected?.total_working_hours), Icon: Clock },
                                    ].map((item) => (
                                        <div key={item.label} className="rounded-md border border-border bg-muted/20 px-2 py-2">
                                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                                                <item.Icon className="h-3 w-3" />
                                                {item.label}
                                            </p>
                                            <p className="text-sm font-semibold tabular-nums mt-1 text-gray-900 dark:text-white">
                                                {item.value || "—"}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
                                        Punch trail
                                    </p>
                                    {punches.length === 0 ? (
                                        <p className="text-sm text-muted-foreground rounded-md border border-dashed border-border px-3 py-6 text-center">
                                            No punches on this day.
                                        </p>
                                    ) : (
                                        <ol className="space-y-2 overflow-y-auto overscroll-contain max-h-[14rem] pr-1">
                                            {punches.map((punch, index) => {
                                                const direction = String(punch.direction || "").toUpperCase();
                                                const inbound = direction === "IN";
                                                return (
                                                    <li
                                                        key={`${punch.at || punch.time}-${index}`}
                                                        className="flex items-center gap-3 rounded-md border border-border px-3 py-2 bg-background"
                                                    >
                                                        <span className={cn(
                                                            "h-7 w-7 rounded-full grid place-items-center shrink-0",
                                                            inbound
                                                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                                                : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
                                                        )}>
                                                            {inbound ? <LogIn className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}
                                                        </span>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                                                                {punch.time || formatClock(punch.at) || "—"}
                                                            </p>
                                                            <p className="text-[11px] text-muted-foreground truncate">
                                                                {direction || "Punch"}
                                                                {viaLabel(punch) ? ` · ${viaLabel(punch)}` : ""}
                                                            </p>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ol>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="rounded-lg border border-border overflow-hidden">
                <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 border-b border-border bg-muted/20">
                    <div className="flex items-start gap-2 min-w-0">
                        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                Attendance penalties
                            </h3>
                            <p className="text-xs text-muted-foreground">
                                Late arrival and short-duty penalties for {month.format("MMMM YYYY")}.
                                {canEditPenalties
                                    ? " Accept the suggestion or override the value."
                                    : ""}
                            </p>
                        </div>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                        {penalties.length} {penalties.length === 1 ? "record" : "records"}
                    </span>
                </div>

                {penaltiesLoading && penalties.length === 0 ? (
                    <div className="flex items-center justify-center py-10">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sidebar-primary" />
                    </div>
                ) : penalties.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-4 py-8 text-center">
                        No attendance penalties for this employee in {month.format("MMMM YYYY")}.
                    </p>
                ) : (
                    <div className="overflow-y-auto overscroll-contain max-h-[16rem]">
                        <table className="min-w-full text-sm">
                            <thead className="bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground sticky top-0">
                                <tr>
                                    <th className="px-3 py-2 font-medium">Date</th>
                                    <th className="px-3 py-2 font-medium">Type</th>
                                    <th className="px-3 py-2 font-medium">Late</th>
                                    <th className="px-3 py-2 font-medium">Penalty</th>
                                    <th className="px-3 py-2 font-medium">Value</th>
                                    <th className="px-3 py-2 font-medium">Status</th>
                                    <th className="px-3 py-2 font-medium">Reason</th>
                                    {canEditPenalties && (
                                        <th className="px-3 py-2 font-medium text-right">Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {penalties.map((row) => {
                                    const key = penaltyDateKey(row.date);
                                    const isSelected = selectedKey === key;
                                    const pending = row.review_status !== "Reviewed";
                                    return (
                                        <tr
                                            key={row.attendance_id || row.id}
                                            className={cn(
                                                "cursor-pointer hover:bg-muted/30",
                                                isSelected && "bg-sidebar-primary/5",
                                            )}
                                            onClick={() => key && setSelectedKey(key)}
                                        >
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {row.date ? dayjs(row.date).format("DD MMM YYYY") : "—"}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-xs font-medium">
                                                    {row.penalty_type || "—"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 tabular-nums text-muted-foreground">
                                                {row.late_minutes > 0 ? `${row.late_minutes} min` : "—"}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={cn(
                                                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                    row.penalty === "Fine"
                                                        ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                                                        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
                                                )}>
                                                    {row.penalty || "—"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 font-medium whitespace-nowrap">
                                                {row.value || "—"}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={cn(
                                                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                    pending
                                                        ? "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                                        : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
                                                )}>
                                                    {row.review_status || "—"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-muted-foreground max-w-[14rem] truncate" title={row.reason || ""}>
                                                {row.reason && row.reason !== "-" ? row.reason : "—"}
                                            </td>
                                            {canEditPenalties && (
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                                        {pending && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                                                                disabled={acceptingId === row.attendance_id}
                                                                onClick={() => acceptPenalty(row)}
                                                                title="Accept suggested penalty"
                                                            >
                                                                {acceptingId === row.attendance_id
                                                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                                                    : <Check className="h-4 w-4" />}
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                                            onClick={() => openPenaltyEdit(row)}
                                                            title={pending ? "Override penalty" : "Edit penalty"}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <DialogModal
                open={editOpen}
                onClose={closeDay}
                onSave={saveDay}
                saveLabel="Save"
                loading={savingDay}
                title="Edit Attendance"
                width="72rem"
            >
                <AttendanceEdit
                    editDetails={selectedDayRow}
                    ref={attendanceEditRef}
                    onSuccess={closeDay}
                />
            </DialogModal>

            <DialogModal
                open={penaltyDialogOpen}
                onClose={() => {
                    setPenaltyDialogOpen(false);
                    setPenaltyRow(null);
                }}
                onSave={savePenalty}
                title="Override Attendance Penalty"
                closeLabel="Cancel"
                saveLabel="Confirm"
                editMode={true}
                loading={savingPenalty}
                width="440px"
            >
                <div className="space-y-4">
                    <div className="rounded-lg border border-border p-3 bg-muted/30">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {penaltyRow?.date ? dayjs(penaltyRow.date).format("DD MMM YYYY") : "—"}
                            {penaltyRow?.penalty_type ? ` · ${penaltyRow.penalty_type}` : ""}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                            Adjust the value below, then confirm. Use Accept on the list when the suggested value is fine.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Penalty Mode
                        </label>
                        <Select
                            size="large"
                            className="w-full"
                            value={penaltyForm.mode}
                            onChange={(value) => setPenaltyForm((prev) => ({ ...prev, mode: value }))}
                        >
                            <Option value="deduction">Deduction (Loss of Pay)</Option>
                            <Option value="fine">Fine (Flat Amount)</Option>
                        </Select>
                    </div>

                    {penaltyForm.mode === "fine" ? (
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                                <IndianRupee className="h-3 w-3" /> Fine Amount
                            </label>
                            <Input
                                type="number"
                                min={0}
                                step={1}
                                value={penaltyForm.fine}
                                onChange={(e) => setPenaltyForm((prev) => ({
                                    ...prev,
                                    fine: Number(e.target.value),
                                }))}
                            />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Day Fraction (0 = waive, 0.5 = half day, 1 = full day)
                            </label>
                            <Input
                                type="number"
                                min={0}
                                max={1}
                                step={0.25}
                                value={penaltyForm.deduction}
                                onChange={(e) => setPenaltyForm((prev) => ({
                                    ...prev,
                                    deduction: Number(e.target.value),
                                }))}
                            />
                        </div>
                    )}
                </div>
            </DialogModal>
        </div>
    );
};

export default EmployeeAttendanceContent;
